import React, { useEffect, useMemo, useState } from "react";
import { FaChevronRight, FaTimes } from "react-icons/fa";
import { ACCENT_STYLES, getBadgeValue } from "../navbar4Config";

const Navbar4MegaMenu = ({
  open,
  sections,
  counts,
  onClose,
  onNavigate,
  isVisitor,
  roleLabel,
}) => {
  const [activeSectionId, setActiveSectionId] = useState(sections[0]?.id || null);

  useEffect(() => {
    if (!sections.find((section) => section.id === activeSectionId)) {
      setActiveSectionId(sections[0]?.id || null);
    }
  }, [sections, activeSectionId]);

  const activeSection = useMemo(() => {
    return (
      sections.find((section) => section.id === activeSectionId) ||
      sections[0] ||
      null
    );
  }, [sections, activeSectionId]);

  if (!open) return null;

  const activeAccent =
    ACCENT_STYLES[activeSection?.accent] || ACCENT_STYLES.blue;

  return (
    <>
      <button
        type="button"
        aria-label="Fermer le menu"
        className="fixed inset-0 z-[55] hidden bg-slate-950/55 backdrop-blur-sm md:block"
        onClick={onClose}
      />

      <div className="fixed left-1/2 top-[84px] z-[60] hidden w-[min(1180px,calc(100vw-48px))] -translate-x-1/2 overflow-hidden rounded-[2rem] border border-slate-200 bg-[#f8fafc] shadow-2xl dark:border-white/10 dark:bg-[#070b14] md:block">
        <div className="grid max-h-[calc(100vh-112px)] grid-cols-[325px_1fr] overflow-hidden">
          <aside className="border-r border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-[#0b1220]">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-sky-600 dark:text-sky-300">
                  Menu principal
                </p>
                <h2 className="mt-2 font-heading text-[2rem] font-bold leading-tight text-slate-950 dark:text-white">
                  {isVisitor ? "Découvrir" : `Espace ${roleLabel}`}
                </h2>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-100 dark:border-white/10 dark:bg-white/[0.06] dark:text-white/70 dark:hover:bg-white/[0.1]"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-3">
              {sections.map((section) => {
                const accent = ACCENT_STYLES[section.accent] || ACCENT_STYLES.blue;
                const active = section.id === activeSection?.id;

                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveSectionId(section.id)}
                    className={`flex w-full items-center gap-4 rounded-2xl border px-4 py-4 text-left transition-all ${
                      active
                        ? `${accent.soft} ${accent.border} ${accent.leftBorder} shadow-sm`
                        : "border-slate-200 bg-white hover:bg-slate-50 dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.05]"
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block text-[1rem] font-bold ${
                          active
                            ? accent.text
                            : "text-slate-800 dark:text-white/80"
                        }`}
                      >
                        {section.title}
                      </span>
                      <span className="mt-2 block text-[13px] font-medium leading-5 text-slate-500 dark:text-white/45">
                        {section.subtitle}
                      </span>
                    </span>

                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 dark:bg-white/[0.07] dark:text-white/50">
                      {section.items.length}
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="overflow-y-auto p-6">
            {activeSection && (
              <>
                <div
                  className={`mb-6 rounded-[1.8rem] border ${activeAccent.border} ${activeAccent.soft} p-6`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p
                        className={`text-[11px] font-bold uppercase tracking-[0.28em] ${activeAccent.text}`}
                      >
                        {activeSection.title}
                      </p>
                      <h3 className="mt-3 font-heading text-[2.6rem] font-bold leading-tight text-slate-950 dark:text-white">
                        {activeSection.subtitle}
                      </h3>
                      <p className="mt-3 max-w-2xl text-[15px] font-medium leading-7 text-slate-600 dark:text-white/55">
                        {activeSection.description}
                      </p>
                    </div>

                    <div
                      className={`hidden rounded-2xl bg-gradient-to-r ${activeAccent.gradient} px-4 py-2.5 text-sm font-bold text-white shadow-sm lg:block`}
                    >
                      {activeSection.items.length} accès
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  {activeSection.items.map((item) => {
                    const Icon = item.icon;
                    const badge = getBadgeValue(item, counts);

                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => onNavigate(item.path, item.roles)}
                        className={`group flex min-h-[104px] items-center gap-4 rounded-[1.4rem] border bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:bg-[#101827] ${
                          item.featured
                            ? `${activeAccent.border} ring-2 ring-offset-0 ${activeAccent.border}`
                            : "border-slate-200 dark:border-white/10"
                        }`}
                      >
                        <span
                          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${activeAccent.iconSoft}`}
                        >
                          {Icon ? <Icon className="text-lg" /> : null}
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate font-heading text-[1.02rem] font-bold text-slate-950 dark:text-white">
                              {item.label}
                            </span>

                            {/* {item.soon && (
                              <span className="rounded-full bg-slate-950 px-2 py-0.5 text-[10px] font-bold text-white dark:bg-white dark:text-slate-950">
                                bientôt
                              </span>
                            )} */}
                          </span>

                          <span className="mt-2 block text-[14px] font-medium leading-6 text-slate-500 dark:text-white/45">
                            {item.description}
                          </span>
                        </span>

                        {badge > 0 && (
                          <span className="rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold text-white">
                            {badge > 99 ? "99+" : badge}
                          </span>
                        )}

                        <FaChevronRight className="text-xs text-slate-400 transition-transform group-hover:translate-x-1 dark:text-white/35" />
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default Navbar4MegaMenu;